import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard/admin');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        Dashboard Administrateur
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Admins</h3>
          <p className="text-4xl font-bold">{stats?.admins?.total || 0}</p>
          <p className="text-sm mt-2">
            {stats?.admins?.active || 0} actifs
          </p>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <h3 className="text-lg font-semibold mb-2">User Owners</h3>
          <p className="text-4xl font-bold">{stats?.owners?.total || 0}</p>
          <p className="text-sm mt-2">
            {stats?.owners?.active || 0} actifs
          </p>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <h3 className="text-lg font-semibold mb-2">User Simples</h3>
          <p className="text-4xl font-bold">{stats?.simples?.total || 0}</p>
          <p className="text-sm mt-2">
            {stats?.simples?.active || 0} actifs
          </p>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Associations</h3>
          <p className="text-4xl font-bold">{stats?.associations?.total || 0}</p>
          <p className="text-sm mt-2">
            {stats?.associations?.active || 0} actives
          </p>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card 
          className="hover:shadow-lg transition cursor-pointer border-2 border-transparent hover:border-blue-500" 
          onClick={() => navigate('/associations')}
        >
          <div className="flex items-center space-x-4">
            <div className="text-5xl">🔗</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Associations</h3>
              <p className="text-gray-600 text-sm">Gérer les associations Sensor ↔ Switch</p>
            </div>
          </div>
        </Card>

        <Card 
          className="hover:shadow-lg transition cursor-pointer border-2 border-transparent hover:border-green-500" 
          onClick={() => navigate('/owners')}
        >
          <div className="flex items-center space-x-4">
            <div className="text-5xl">👤</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">User Owners</h3>
              <p className="text-gray-600 text-sm">Gérer les propriétaires</p>
            </div>
          </div>
        </Card>

        <Card 
          className="hover:shadow-lg transition cursor-pointer border-2 border-transparent hover:border-orange-500" 
          onClick={() => navigate('/consumption/global')}
        >
          <div className="flex items-center space-x-4">
            <div className="text-5xl">⚡</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Consommation Global</h3>
              <p className="text-gray-600 text-sm">Vue d'ensemble de la consommation</p>
            </div>
          </div>
        </Card>

        <Card 
          className="hover:shadow-lg transition cursor-pointer border-2 border-transparent hover:border-purple-500" 
          onClick={() => navigate('/billing-config')}
        >
          <div className="flex items-center space-x-4">
            <div className="text-5xl">⚙️</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Configuration</h3>
              <p className="text-gray-600 text-sm">Paramètres de facturation</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Devices Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card title="📊 Statistiques des Devices">
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Devices</span>
              <span className="text-2xl font-bold text-blue-600">
                {stats?.devices?.total || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-gray-700 font-medium">Devices en ligne</span>
              <span className="text-2xl font-bold text-green-600">
                {stats?.devices?.online || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
              <span className="text-gray-700 font-medium">Capteurs</span>
              <span className="text-2xl font-bold text-orange-600">
                {stats?.devices?.sensors || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-gray-700 font-medium">Interrupteurs</span>
              <span className="text-2xl font-bold text-purple-600">
                {stats?.devices?.switches || 0}
              </span>
            </div>
          </div>
        </Card>

        <Card title="📝 Activités Récentes">
          {stats?.recentLogs?.length > 0 ? (
            <div className="space-y-2">
              {stats.recentLogs.slice(0, 5).map((log) => (
                <div
                  key={log._id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                >
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">
                      {log.actorName}
                    </p>
                    <p className="text-xs text-gray-600">{log.details}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(log.createdAt).toLocaleTimeString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Aucune activité récente
            </p>
          )}
        </Card>
      </div>

      {/* Associations Récentes */}
      <Card title="🔗 Associations Récentes">
        {stats?.recentAssociations?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.recentAssociations.map((assoc) => (
              <div
                key={assoc._id}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 hover:shadow-lg transition cursor-pointer"
                onClick={() => navigate(`/associations/${assoc._id}/dashboard`)}
              >
                <h4 className="font-bold text-gray-800 mb-2">{assoc.name}</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs">📡 {assoc.sensorId?.name || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-xs">💡 {assoc.switchId?.name || 'N/A'}</span>
                </div>
                <div className="flex space-x-2">
                  {assoc.active && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                      Actif
                    </span>
                  )}
                  {assoc.autoMode && (
                    <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                      Auto
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            Aucune association récente
          </p>
        )}
      </Card>


    </div>
  );
};

export default AdminDashboard;